import moment from 'moment/moment';
import { useMemo } from 'react';
import { SWRConfiguration } from 'swr';

import { Roles } from '~/data/user/role';
import { useArraySWR } from '~/hooks/useSWR';
import { fetchFromOldApi } from '~/services/Http';

export enum AuditType {
  AbsenceShort = 'absence_short',
  AbsenceLong = 'absence_long',
  AuthUser = 'auth_user',
  AuthUserRole = 'auth_user_role',
  AuthResponsibility = 'auth_responsibility',
  CustomData = 'custom_data',
  EmployeeContactHome = 'employee_contact_home', // Compacted audit type
  EmployeeContactWork = 'employee_contact_work', // Compacted audit type
  EmployeeContactOther = 'employee_contact_other', // Compacted audit type
  EmployeeHomeContact = 'employee_home_contact',
  EmployeeHomeAddress = 'employee_home_address',
  EmployeeHomeEmail = 'employee_home_email',
  EmployeeHomePhone = 'employee_home_phone',
  EmployeeWorkContact = 'employee_work_contact',
  EmployeeWorkEmail = 'employee_work_email',
  EmployeeWorkPhone = 'employee_work_phone',
  EmployeeWorkMobile = 'employee_work_mobile_phone',
  EmployeeOtherContact = 'employee_other_contact', // Probable upcoming type
  EmployeeOtherAddress = 'employee_other_address',
  EmployeeOtherEmail = 'employee_other_email',
  EmployeeOtherPhone = 'employee_other_phone',
  Employment = 'employment',
  EmploymentPosition = 'position',
  EmploymentDetail = 'employment_detail',
  PersonProfile = 'person_profile',
}

export enum AuditTargetType {
  Employee = 'employee',
  Entity = 'entity',
}

export enum AuditOperation {
  Update = 'U',
  Insert = 'I',
  Delete = 'D',
}

type AuditSummaryData_Common = {
  operation: AuditOperation;
  timestamp: string;
  subjectUserId: string; // Number
  subjectEmployeeId: string; // Number
  targetId: string; // Number
  transactionId?: string;
};

export type AuditSummaryData_Employee_AuthResponsibility_Data = {
  index: number;
  unit_fk: number;
  user_fk: number;
  role_name: Roles;
  employee_fk: number;
};

export type AuditSummaryData_Employee_AuthResponsibility =
  AuditSummaryData_Common & {
    targetType: AuditTargetType.Employee;
    auditType: AuditType.AuthResponsibility;
    data: AuditSummaryData_Employee_AuthResponsibility_Data;
  };

export type AuditSummaryData_Employee_AuthUserRole_Data = {
  ips: null | unknown;
  user_fk: number;
  role_name: Roles;
};

export type AuditSummaryData_Employee_AuthUserRole = AuditSummaryData_Common & {
  targetType: AuditTargetType.Employee;
  auditType: AuditType.AuthUserRole;
  data: AuditSummaryData_Employee_AuthUserRole_Data;
};

export type AuditSummaryData_Employee_AuthUser_Data = {
  id: number;
  status: string;
  password: string;
  username?: string;
  profile_fk: number;
  employee_fk: number;
  is_external: boolean;
  language_fk: string;
  is_double_hashed: boolean;
  email_notify_freq_fk: string;
  last_successful_login_ip: string; // IP
  last_successful_login_ts: string; // Date Time
  _email_notify_freq_grp_fk: string;
  current_successful_login_ip: string; // IP
  current_successful_login_ts: string; // Date Time
};

export type AuditSummaryData_Employee_AuthUser = AuditSummaryData_Common & {
  targetType: AuditTargetType.Employee;
  auditType: AuditType.AuthUser;
  data: AuditSummaryData_Employee_AuthUser_Data;
};

export type AuditSummaryData_Employee_Absences_Data = {
  id: number;
  uid: string;
  info?: string;
};

export type AuditSummaryData_Employee_Absences = AuditSummaryData_Common & {
  targetType: AuditTargetType.Employee;
  auditType: AuditType.AbsenceLong | AuditType.AbsenceShort;
  data: AuditSummaryData_Employee_Absences_Data;
};

export type AuditSummaryData_Employee_Other = AuditSummaryData_Common & {
  targetType: AuditTargetType.Employee;
  auditType:
    | AuditType.CustomData
    | AuditType.EmployeeWorkContact
    | AuditType.EmployeeWorkEmail
    | AuditType.EmployeeWorkPhone
    | AuditType.EmployeeWorkMobile
    | AuditType.EmployeeHomeContact
    | AuditType.EmployeeHomeAddress
    | AuditType.EmployeeHomeEmail
    | AuditType.EmployeeHomePhone
    | AuditType.EmployeeOtherContact
    | AuditType.EmployeeOtherAddress
    | AuditType.EmployeeOtherEmail
    | AuditType.EmployeeOtherPhone
    | AuditType.Employment
    | AuditType.EmploymentDetail
    | AuditType.EmploymentPosition
    | AuditType.PersonProfile
    | string;
  data: object;
};

export type AuditSummaryData_Employee =
  | AuditSummaryData_Employee_AuthUser
  | AuditSummaryData_Employee_AuthUserRole
  | AuditSummaryData_Employee_AuthResponsibility
  | AuditSummaryData_Employee_Absences
  | AuditSummaryData_Employee_Other;

export type AuditSummaryData_EntityData = object & {
  id: number;
  entity_fk: string;
  entity_id: number;
  attribute_value?: string | number;
  attribute_name_fk: string;
  attribute_enum_grp_fk: string | null;
};

export type AuditSummaryData_Entity = AuditSummaryData_Common & {
  targetType: AuditTargetType.Entity;
  auditType: AuditType | string;
  data: AuditSummaryData_EntityData;
};

export type AuditSummaryData_Other = AuditSummaryData_Common & {
  targetType: string;
  auditType: string;
  data: object;
};

export type AuditSummaryData =
  | AuditSummaryData_Employee
  | AuditSummaryData_Entity
  | AuditSummaryData_Other;

type AuditSummaryData_Raw = Omit<AuditSummaryData, 'data'> & { data: string };

const getReportURLParams = <P extends object>(
  props?: P,
): string | undefined => {
  return !!props
    ? Object.entries(props)
        .filter(([_key, value]) => typeof value !== 'undefined')
        .map((keyValuePair) => {
          const [key, value] = keyValuePair;

          const isDate = value instanceof Date;
          const isDateString =
            typeof value === 'string' && moment(value).isValid();

          if (isDate || isDateString) {
            const formattedDate: string = !!value
              ? moment(value as Date).format('YYYY-MM-DD')
              : '';

            return `${key}=${formattedDate}`;
          } else {
            return keyValuePair.join('=');
          }
        })
        .join('&')
    : undefined;
};

const getAuditSummary = (urlParams?: string): Promise<AuditSummaryData_Raw[]> =>
  fetchFromOldApi(
    `/json/audit/summary${urlParams ? `?${urlParams}` : ''}`,
  ).then((r) => r.json());

type ParamsType = {
  target?: number;
  startDate?: Date;
  endDate?: Date;
};

export const useAuditSummary = (
  params: ParamsType,
  swrConfig?: SWRConfiguration,
) => {
  const urlParams = useMemo(() => getReportURLParams(params), [params]);

  return useArraySWR<AuditSummaryData>(
    ['useAuditSummary', urlParams],
    ([_key]) =>
      getAuditSummary(urlParams).then((payload) => {
        return payload.map(({ data, ...rest }): AuditSummaryData => {
          return {
            data: JSON.parse(data),
            ...rest,
          };
        });
      }),
    swrConfig,
  );
};
